import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdviseActionNeededItem, AdviseActionNeededReport } from '../../../types/advise';
import { getProductType } from '../../../constants/advise';
import { DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { ActionNeededBox } from '../../Compounds/ActionNeededBox/ActionNeededBox';
import { ActionNeededItemRow } from '../../Compounds/ActionNeededBox/ActionNeededItemRow';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TextBlock, TextColor, TextSpan } from '../../Atoms/Text';

const ARIA_DISABLED_STYLE = 'aria-disabled:opacity-50 aria-disabled:cursor-not-allowed';
const ENTRY_BUTTON_STYLE = `${DEFAULT_CANCEL_BUTTON_STYLE} !py-1 !px-3 ${ARIA_DISABLED_STYLE}`;

interface EngagementActionNeededBoxProps {
  report: AdviseActionNeededReport | null;
  pendingIds: ReadonlySet<string>;
  onSnooze: (item: AdviseActionNeededItem) => void;
  onClear: (item: AdviseActionNeededItem) => void;
}

const summaryFor = (count: number) =>
  `${count} ${count === 1 ? 'engagement requires' : 'engagements require'} your follow-up — ` +
  'review the situation and recommended action below.';

const detailFor = (item: AdviseActionNeededItem) =>
  [getProductType(item.product_type)?.name ?? item.product_type, item.customer_name, item.owner_name]
    .filter(Boolean)
    .join(' · ');

interface EntryControlsProps {
  item: AdviseActionNeededItem;
  pending: boolean;
  onSnooze: (item: AdviseActionNeededItem) => void;
  onClear: (item: AdviseActionNeededItem) => void;
}

// aria-disabled rather than disabled: a disabled button drops keyboard focus, which would land back at the page's top.
const EntryControls = ({ item, pending, onSnooze, onClear }: EntryControlsProps) => {
  const [pressed, setPressed] = useState<string | null>(null);

  const control = (label: string, onPress: (item: AdviseActionNeededItem) => void) => (
    <button
      type="button"
      aria-label={label}
      aria-disabled={pending}
      className={ENTRY_BUTTON_STYLE}
      onClick={() => {
        if (pending) return;
        setPressed(label);
        onPress(item);
      }}>
      {pending && pressed === label ? <ComponentSpinner sx={{ height: '19px' }} /> : label}
    </button>
  );

  return (
    <div className="flex gap-2">
      {item.can_snooze && control('Snooze', onSnooze)}
      {control('Clear', onClear)}
    </div>
  );
};

const EntryLine = ({ label, labelColor, text }: { label: string; labelColor: TextColor; text: string }) => (
  <TextBlock
    size="sm"
    color="grey9"
    className="mt-1">
    <TextSpan
      size="xs"
      weight="semibold"
      color={labelColor}
      className="mr-3">
      {label}
    </TextSpan>
    {text}
  </TextBlock>
);

export const EngagementActionNeededBox = ({
  report,
  pendingIds,
  onSnooze,
  onClear
}: EngagementActionNeededBoxProps) => {
  const navigate = useNavigate();

  if (!report || report.items.length === 0) return null;

  return (
    <ActionNeededBox
      title="Action Needed"
      count={`${report.action_needed_count} of ${report.total_engagements} engagements need attention`}
      summary={summaryFor(report.action_needed_count)}
      testIdPrefix="action-needed"
      contentKey={report}
      collapsible>
      {report.items.map((item) => (
        <ActionNeededItemRow
          key={`${item.engagement_id}-${item.condition}`}
          testIdPrefix="action-needed"
          itemId={item.engagement_id}
          onOpen={() => navigate(`/advise/analysis?id=${item.engagement_id}`)}
          className="border-l-[3px] border-[#D96E2B] pl-4"
          actions={
            report.can_manage && (
              <EntryControls
                item={item}
                pending={pendingIds.has(item.engagement_id)}
                onSnooze={onSnooze}
                onClear={onClear}
              />
            )
          }>
          <TextBlock
            size="sm"
            weight="semibold"
            color="grey9">
            {item.engagement_name} — {item.label}
          </TextBlock>
          <TextBlock
            size="sm"
            color="grey10">
            {detailFor(item)}
          </TextBlock>
          <EntryLine
            label="RISK"
            labelColor="orange"
            text={item.risk_message}
          />
          <EntryLine
            label="ACTION"
            labelColor="indigo"
            text={item.action_message}
          />
        </ActionNeededItemRow>
      ))}
    </ActionNeededBox>
  );
};
